import { useEffect, useRef, useState, type ReactElement } from "react";
import { audio } from "../../lib/audio";
import type { DevDayConfig, MfitGuideText, ScreenBox } from "../../scenario/types";
import type { MfitDone } from "../mfit/shared";
import { Glass, MissionIntro, Win, type DdIntroData } from "./ui";
import type { DevDayCfg, StageProps } from "./types";
import { Briefing, D1Chat, D1Context, D2, D3, Film } from "./missionsA";
import { D4, D5Patch, D5Test, D6, D7 } from "./missionsB";
import { D10Console, D10Evidence, D8Ci, D8Matrix, D9, Replay } from "./missionsC";
import { Lab } from "./labs";
import { dd, MISSION_ORDER } from "./state";
import { useSim } from "../../state/store";
import { DD_ENTRY } from "../../scenario/the-1700-release.content";
import "./devday.css";
import { tx } from "../../lib/tx";

/** The last screen of each mission (and the film), keyed by device mode. */
const LAST_SCREEN: Record<string, string> = {
  film: "film", d1ctx: "d1", d2: "d2", d3: "d3", d4: "d4", d5test: "d5", d6: "d6", d7: "d7", d8ci: "d8", d9: "d9", d10console: "d10",
};

/**
 * "The 17:00 Release" device kit (kind `devday`). One component per mode; each
 * renders a fictional developer application glued to the monitor that owns the
 * beat (Beat.screenQuad -> App screenBox). Nothing here runs code, calls a
 * model, installs a package or deploys anything: every button changes course
 * state and synthetic fixtures only.
 */
const STAGES: Record<string, (p: StageProps) => ReactElement | null> = {
  briefing: Briefing,
  film: Film,
  d1chat: D1Chat,
  d1ctx: D1Context,
  d2: D2,
  d3: D3,
  d4: D4,
  d5patch: D5Patch,
  d5test: D5Test,
  d6: D6,
  d7: D7,
  d8matrix: D8Matrix,
  d8ci: D8Ci,
  d9: D9,
  d10evidence: D10Evidence,
  d10console: D10Console,
  replay: Replay,
  lab: Lab,
};

export function DevDay({
  config,
  screenBox,
  onGuideChange,
  onDone,
  onRecord,
}: {
  config: DevDayConfig;
  screenBox?: ScreenBox | null;
  onGuideChange?: (g: MfitGuideText | null) => void;
  onDone: (d: MfitDone) => void;
  onRecord?: (eventId: string, evidence?: string) => void;
}) {
  const doneRef = useRef(false);
  const cfg = config as unknown as DevDayCfg;
  const raw = (cfg.content as { intro?: unknown } | null)?.intro;
  const intro = raw && typeof raw === "object" && "situation" in raw ? (raw as DdIntroData) : undefined;
  const [introDone, setIntroDone] = useState(!intro);
  // Keep the music bed well under the narration for this whole course so the
  // voice is never masked. Persistent for the scene; a page load resets it.
  useEffect(() => { audio.duckScene(0.45); }, []);
  const Stage = STAGES[cfg.mode];
  if (!Stage) return null;
  const finish = (key?: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    const k = key ?? "continue";
    // After a mission's last screen, follow the learner's route: jump to the
    // next mission in it (or the 17:00 replay) when that is not the next one
    // in course order anyway.
    const m = LAST_SCREEN[cfg.mode];
    if (k === "continue" && m) {
      const idx = m === "film" ? -1 : MISSION_ORDER.indexOf(m);
      const natural = MISSION_ORDER[idx + 1] ?? "replay";
      const route = dd.route();
      const next = MISSION_ORDER.slice(idx + 1).find((x) => route.includes(x)) ?? "replay";
      if (next !== natural && DD_ENTRY[next]) {
        useSim.getState().goToBeat(DD_ENTRY[next]);
        return;
      }
    }
    onDone({ key: k });
  };
  const rec = (eventId: string, evidence?: string) => onRecord?.(eventId, evidence);
  return (
    <Glass box={screenBox} portrait={cfg.portrait}>
      {!introDone && intro ? (
        <Win app={tx("Mission briefing")} tag={cfg.screen.toUpperCase()} clock={cfg.clock}>
          <MissionIntro intro={intro} portrait={cfg.portrait} onGuideChange={onGuideChange} onStart={() => { audio.ui("pageOpen"); setIntroDone(true); }} />
        </Win>
      ) : (
        <Stage cfg={cfg} finish={finish} onGuideChange={onGuideChange} rec={rec} />
      )}
    </Glass>
  );
}
